import { isOwnMutation } from './mutation-tracker';
import { type RealtimeEvent } from './realtime-event';
import { type RealtimeHandlers, useRealtime } from './use-realtime';

/** A single realtime event handler, invoked only for events that aren't our own echo. */
export type RealtimeEventHandler = (event: RealtimeEvent<unknown>) => void;

/**
 * Build the handlers map from a typed event→payload contract, so each reconciler's
 * `event.data` is the payload its event actually carries rather than `unknown`
 * hand-cast per handler (TAS-150). The socket has no generated schema, so the map
 * `M` — defined per channel by the caller — is the hand-maintained contract, but
 * it's one checked declaration instead of a scattered `as`. `useRealtimeEvents`
 * keeps its loose `Record<string, RealtimeEventHandler>` signature so the dynamic
 * `useRealtimeList` reconcilers can still register handlers by computed event name.
 */
export function realtimeHandlers<M>(handlers: {
	[K in keyof M]: (event: RealtimeEvent<M[K]>) => void;
}): Record<string, RealtimeEventHandler> {
	return handlers as unknown as Record<string, RealtimeEventHandler>;
}

/**
 * The cache-shape-agnostic core every live reconciler shares: subscribe to the
 * named events, drop our own echoes (already applied optimistically) before
 * dispatching, and fire `onReconnect` after a dropped connection so the caller
 * can refetch what it missed. Callers own the actual cache update per event, so
 * this serves flat lists, keyset pages, and the grouped board alike.
 *
 * `applyOwnEchoes` opts out of the own-echo drop for callers whose cache has no
 * optimistic path to skip — the epic reconciler needs it, because an epic's
 * progress moves as a *side effect* of a task mutation (this tab optimistically
 * updates the board, never the epic counts), so its own echo is the only signal
 * the acting tab gets. It's safe there because the reconciler is version-aware
 * (see `mergeEpic`): an equal-version progress echo takes the fresh counts, and
 * a version-bumped field edit — already applied optimistically — keeps them. Do
 * not enable it for reconcilers that DO update their cache optimistically.
 */
export function useRealtimeEvents(opts: {
	url: string;
	handlers: Record<string, RealtimeEventHandler>;
	enabled?: boolean;
	onReconnect?: () => void;
	onConnect?: () => void;
	applyOwnEchoes?: boolean;
}): void {
	const { url, handlers, enabled = true, onReconnect, onConnect, applyOwnEchoes = false } = opts;

	const wrapped: RealtimeHandlers = {};
	for (const [name, handle] of Object.entries(handlers)) {
		wrapped[name] = (payload) => {
			const event = payload as RealtimeEvent<unknown> | undefined;
			if (!event || (!applyOwnEchoes && isOwnMutation(event.mutationId ?? null))) {
				return;
			}
			handle(event);
		};
	}

	useRealtime(url, wrapped, enabled, onReconnect, onConnect);
}

/**
 * True when `incoming` is not newer than what we already hold, so applying it
 * would be a stale or out-of-order write. A no-op for entities without a version
 * (both undefined), so unversioned lists keep last-write-wins behaviour.
 */
export function isStaleVersion(
	existing: { version?: number } | undefined,
	incoming: { version?: number },
): boolean {
	return (
		existing?.version !== undefined &&
		incoming.version !== undefined &&
		existing.version >= incoming.version
	);
}
